import WEAPONS from "../consts/WEAPONS";
import WeaponType from "../enums/WeaponType";
import Game from "./Game";
import PICKUPS from "../consts/PICKUPS";
import PickupType from "../enums/PickupType";
import { isPowerupUnlocked } from "../consts/CHARACTER_PROGRESSION";
import { isWeaponUnlocked } from "../consts/CHARACTER_PROGRESSION";

export interface WeaponData {
  level: number;
  bulletType: WeaponType;
  nameKey?: string;
  name?: string;
  descriptionKey?: string;
  description?: string;
  tipsKey?: string;
  frameName: string;
  iconName: string;
  rarity: number;
  isEvolution?: boolean;
  isUnlocked?: boolean;
  isPowerUp?: boolean;
  requires?: WeaponType;
  evolvesFrom?: WeaponType[];
  // Add other optional properties that might exist
  animationKey?: string | null;
  tips?: string;
  interval?: number;
  repeatInterval?: number;
  power?: number;
  area?: number;
  speed?: number;
  amount?: number;
  duration?: number;
  hitsWalls?: boolean;
  penetrating?: number;
  knockback?: number;
}

interface WeightedItem {
  weaponType: WeaponType;
  weight: number;
}

interface LevelUpFactoryConfig {
  defaultXPfactor: number;
  levelUpOptions: number;
  maxTrueWeapons: number;
  maxPowerUpWeapons: number;
  chanceForExistingPowerUp: number;
}

interface WeightProvider<T> {
  getWeight: (item: T) => number;
  getTotalWeight?: (items: T[]) => number;
}

const DEFAULT_CONFIG: LevelUpFactoryConfig = {
  defaultXPfactor: 5,
  levelUpOptions: 3,
  maxTrueWeapons: 6,
  maxPowerUpWeapons: 6,
  chanceForExistingPowerUp: 0.3,
};

export default class LevelUpFactory {
  private readonly config: LevelUpFactoryConfig;
  private currentXPfactor: number;
  private previousXPfactor: number;
  private weaponStore: WeaponType[];
  private specialWeapons: WeaponType[];
  private accumulatedWeight: number;
  private weightedStore: WeightedItem[];

  constructor(config: Partial<LevelUpFactoryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.currentXPfactor = this.config.defaultXPfactor;
    this.previousXPfactor = this.config.defaultXPfactor;
    this.weaponStore = [];
    this.specialWeapons = [];
    this.accumulatedWeight = 0;
    this.weightedStore = [];
  }

  public calculateXPfactor(): void {
    const currentLevelFactor =
      this.config.defaultXPfactor +
      1.5 * Math.floor(Game.Core.Player.level / 20);
    const previousLevelFactor =
      this.config.defaultXPfactor +
      1.5 * Math.floor((Game.Core.Player.level - 1) / 20);

    this.currentXPfactor =
      Math.min(currentLevelFactor, 8) *
      Game.Core.Player.level *
      Game.Core.Player.level *
      0.7; //decrease the xp factor by 30%
    this.previousXPfactor =
      Math.min(previousLevelFactor, 8) *
      (Game.Core.Player.level - 1) *
      (Game.Core.Player.level - 1) *
      0.7; //decrease the xp factor by 30%
  }

  public get xpRequiredToLevelUp(): number {
    return this.currentXPfactor;
  }

  public get previousXpRequiredToLevelUp(): number {
    return this.previousXPfactor;
  }

  public getLevelUpOptions(): number {
    return Math.random() > 1 / Game.Core.Player.luck
      ? this.config.levelUpOptions + 1
      : this.config.levelUpOptions;
  }

  public get chanceForExistingPowerUp(): number {
    const baseChance = this.config.chanceForExistingPowerUp;
    const levelModifier = Game.Core.Player.level % 2 === 0 ? 2 : 1;
    const luckModifier = 1 - 1 / Game.Core.Player.luck;

    return levelModifier * baseChance + luckModifier;
  }

  public hasPowerupsInStore(): boolean {
    return this.weightedStore.length > 0;
  }

  public init(): void {
    this.calculateXPfactor();
    this.weaponStore = [];

    Object.entries(WEAPONS).forEach(([weaponType, weaponData]) => {
      const baseWeapon = weaponData[0] as WeaponData;

      // Skip weapons that should not be in the store
      if (
        (baseWeapon.isEvolution &&
          !Game.Core.hasWeapon(weaponType as WeaponType)) ||
        !baseWeapon.isUnlocked ||
        baseWeapon.rarity === 0 ||
        !this.isUnlockedForCharacter(weaponType as WeaponType)
      ) {
        return;
      }

      this.weaponStore.push(weaponType as WeaponType);
    });

    this.calculateWeights();
  }

  private createWeightedItem(
    weaponType: WeaponType,
    accumulatedWeight: number
  ): WeightedItem {
    return { weaponType, weight: accumulatedWeight };
  }

  private calculateWeights(): void {
    this.accumulatedWeight = 0;
    this.weightedStore = [];

    Object.entries(WEAPONS).forEach(([weaponType, weaponData]) => {
      if (!this.weaponStore.includes(weaponType as WeaponType)) return;

      const baseWeapon = weaponData[0] as WeaponData;
      const existingWeapon = Game.Core.Weapons.find(
        (w) => w.bulletType === weaponType
      );

      if (baseWeapon.isPowerUp) {
        const powerUpCount = Game.Core.Weapons.filter(
          (w) => w.isPowerUp
        ).length;
        if (!existingWeapon && powerUpCount >= this.config.maxPowerUpWeapons)
          return;
      } else {
        const weaponCount = Game.Core.Weapons.filter(
          (w) => !w.isPowerUp
        ).length;
        if (!existingWeapon && weaponCount >= this.config.maxTrueWeapons)
          return;
      }

      if (!baseWeapon.rarity) return;

      this.accumulatedWeight += baseWeapon.rarity;
      this.weightedStore.push(
        this.createWeightedItem(
          weaponType as WeaponType,
          this.accumulatedWeight
        )
      );
    });
  }

  public getLevelUpPowerups(): WeaponType[] {
    const powerups: WeaponType[] = [];

    // Try to add existing weapons first
    let existingWeapon = this.getRandomExistingWeapon();
    if (existingWeapon) {
      powerups.push(existingWeapon);
    }

    existingWeapon = this.getRandomExistingWeapon();
    if (existingWeapon && !powerups.includes(existingWeapon)) {
      powerups.push(existingWeapon);
    }

    // Get remaining weapons using weighted selection
    const weaponWeightProvider: WeightProvider<WeightedItem> = {
      getWeight: (item) => item.weight,
      getTotalWeight: (items) => items[items.length - 1]?.weight ?? 0,
    };

    const remainingCount = this.getLevelUpOptions() - powerups.length;
    if (remainingCount <= 0) return powerups.reverse();

    const weaponPool =
      Game.Core.Player.level <= 3
        ? this.weightedStore.filter(
            (item) =>
              !WEAPONS[item.weaponType][0].isPowerUp &&
              !powerups.includes(item.weaponType)
          )
        : this.weightedStore.filter(
            (item) => !powerups.includes(item.weaponType)
          );

    const additionalWeapons = this.getMultipleWeightedRandomItems(
      weaponPool,
      remainingCount,
      weaponWeightProvider
    ).map((item) => item.weaponType);

    return [...powerups, ...additionalWeapons].reverse();
  }

  private getWeightedRandomItem(
    items: WeightedItem[],
    weightProvider: WeightProvider<WeightedItem>,
    excludeItems: WeightedItem[] = []
  ): WeightedItem | undefined {
    if (items.length === 0) return undefined;

    // Filter out excluded items by comparing weaponType property
    const excludedTypes = excludeItems.map((item) => item.weaponType);
    const availableItems = items.filter(
      (item) => !excludedTypes.includes(item.weaponType)
    );
    if (availableItems.length === 0) return undefined;

    const totalWeight =
      weightProvider.getTotalWeight?.(availableItems) ??
      availableItems.reduce(
        (sum, item) => sum + weightProvider.getWeight(item),
        0
      );

    const randomWeight = Math.random() * totalWeight;
    let currentWeight = 0;

    for (const item of availableItems) {
      currentWeight += weightProvider.getWeight(item);
      if (currentWeight >= randomWeight) {
        return item;
      }
    }

    return undefined;
  }

  private getMultipleWeightedRandomItems(
    items: WeightedItem[],
    count: number,
    weightProvider: WeightProvider<WeightedItem>
  ): WeightedItem[] {
    const result: WeightedItem[] = [];
    let attempts = 0;
    const maxAttempts = 1000;

    while (
      result.length < count &&
      attempts < maxAttempts &&
      items.length > result.length
    ) {
      attempts++;
      const item = this.getWeightedRandomItem(items, weightProvider, result);
      if (item) result.push(item);
    }

    return result.reverse();
  }

  public getLevelUpItems(): PickupType[] {
    const playerLevel = Game.Core.Player.level;

    // Filter available items
    const availableItems = Object.entries(PICKUPS)
      .filter(
        ([_, itemData]) =>
          itemData &&
          !itemData.hidden &&
          playerLevel >= itemData.unlocksAt &&
          itemData.rarity > 0
      )
      .map(([pickupType]) => pickupType as PickupType);

    // If we don't have enough unique items, return what we have
    const targetCount = Math.min(
      this.getLevelUpOptions(),
      availableItems.length
    );

    const pickupWeightProvider: WeightProvider<PickupType> = {
      getWeight: (itemType) => {
        const itemData = PICKUPS[itemType];
        return itemData.isRare
          ? itemData.rarity * Game.Core.Player.luck
          : itemData.rarity;
      },
    };

    // Convert pickups to WeightedItems for our weighted selection system
    const weightedItems: WeightedItem[] = availableItems.map((itemType) => ({
      weaponType: itemType as unknown as WeaponType,
      weight: pickupWeightProvider.getWeight(itemType),
    }));

    const selectedItems = this.getMultipleWeightedRandomItems(
      weightedItems,
      targetCount,
      {
        getWeight: (item) => item.weight,
      }
    );

    return selectedItems.map(
      (item) => item.weaponType as unknown as PickupType
    );
  }

  public removeFromStore(weaponType: WeaponType): void {
    const index = this.weaponStore.indexOf(weaponType);
    if (index > -1) {
      this.weaponStore.splice(index, 1);
      this.calculateWeights();
    }
  }

  public getRandomExistingWeapon(): WeaponType | null {
    if (Math.random() > this.chanceForExistingPowerUp) return null;

    const existingWeapons = Game.Core.Weapons.map(
      (weapon) => weapon.bulletType
    );
    const shuffledWeapons = [...existingWeapons];

    while (shuffledWeapons.length > 0) {
      const randomIndex =
        Math.random() > 0.5
          ? 0
          : Math.floor(Math.random() * shuffledWeapons.length);
      const weaponType = shuffledWeapons[randomIndex];
      const weapon = Game.Core.Weapons.find((w) => w.bulletType === weaponType);
      const level = weapon?.level || 0;

      if (
        2.5 * (level + 1) <= Game.Core.Player.level &&
        this.weaponStore.includes(weaponType)
      ) {
        return weaponType;
      }

      shuffledWeapons.splice(randomIndex, 1);
    }

    return null;
  }

  public addLateWeapon(weaponType: WeaponType): void {
    if (this.isUnlockedForCharacter(weaponType)) {
      this.specialWeapons.push(weaponType);
      if (!this.weaponStore.includes(weaponType)) {
        this.weaponStore.push(weaponType);
        this.calculateWeights();
      }
    }
  }

  private isUnlockedForCharacter(weaponType: WeaponType): boolean {
    const character = Game.Core.UserCharactersService?.getCharacter();
    if (!character) return true;

    const totalUpgradeLevel = character.getTotalUpgrades();

    const weaponData = WEAPONS[weaponType][0];
    if (weaponData.isPowerUp) {
      return isPowerupUnlocked(weaponType, totalUpgradeLevel);
    } else {
      return isWeaponUnlocked(weaponType, totalUpgradeLevel);
    }
  }
}
